import { useMemo, useState } from "react";

/**
 * A donut chart.
 *
 * Hand-drawn SVG rather than ApexCharts. Apex rendered this dashboard's bars at
 * widths that did not match the data — measured against the DOM with animation
 * disabled — and a chart nobody can trust is worse than a table. Here the
 * geometry is one arc per slice from stroke-dasharray, so a slice's angle IS
 * its share and there is nothing to get wrong.
 *
 * The hole is the point: it holds the one figure the slices are the working
 * for, so the reader gets the answer without decoding the ring.
 */
export default function DonutChart({ segments, centerValue, centerLabel, size = 168, thickness = 22 }) {
  const [active, setActive] = useState(null);

  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;

  const arcs = useMemo(() => {
    let offset = 0;
    return segments
      .filter((s) => s.value > 0)
      .map((s) => {
        const share = total > 0 ? s.value / total : 0;
        const arc = {
          ...s,
          share,
          length: share * circumference,
          // Negative because SVG strokes run clockwise from 3 o'clock and the
          // group below is rotated to start at 12.
          offset: -offset * circumference,
        };
        offset += share;
        return arc;
      });
  }, [segments, total, circumference]);

  return (
    <div className="mcap-donut">
      <div className="mcap-donut-figure" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={centerLabel}>
          <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              strokeWidth={thickness}
              className="mcap-donut-track"
            />
            {arcs.map((arc) => (
              <circle
                key={arc.key}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={arc.color}
                strokeWidth={active && active !== arc.key ? thickness - 4 : thickness}
                strokeDasharray={`${arc.length} ${circumference - arc.length}`}
                strokeDashoffset={arc.offset}
                className="mcap-donut-arc"
                opacity={active && active !== arc.key ? 0.45 : 1}
                onMouseEnter={() => setActive(arc.key)}
                onMouseLeave={() => setActive(null)}
              >
                <title>{`${arc.label}: ${arc.value} (${Math.round(arc.share * 1000) / 10}%)`}</title>
              </circle>
            ))}
          </g>
        </svg>

        <div className="mcap-donut-center">
          <p className="mcap-donut-value">{centerValue}</p>
          <p className="mcap-donut-label">{centerLabel}</p>
        </div>
      </div>

      <ul className="mcap-donut-legend">
        {arcs.map((arc) => (
          <li
            key={arc.key}
            className={active === arc.key ? "is-active" : undefined}
            onMouseEnter={() => setActive(arc.key)}
            onMouseLeave={() => setActive(null)}
          >
            <span className="mcap-donut-dot" style={{ backgroundColor: arc.color }} />
            <span className="mcap-donut-legend-label">{arc.label}</span>
            <span className="mcap-donut-legend-value">
              {arc.value}
              <em>{Math.round(arc.share * 1000) / 10}%</em>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
